import { AssessmentCard } from './components/AssessmentCard'
import { LearningPath } from './components/LearningPath'
import { ResultsCard } from './components/ResultsCard'
import { WelcomeHero } from './components/WelcomeHero'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { useEffect, useState } from 'react'
import { analyticsApi, assessmentApi } from '@/utils/api'
import type { UserDashboardData, AssessmentHistoryItem } from '@/types'
import { useNavigate } from 'react-router-dom'
import { resolveBackendUrl } from '@/utils/media'

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [dashboardData, setDashboardData] = useState<UserDashboardData | null>(null)
  const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const coupleId = (user as { coupleId?: string })?.coupleId

  useEffect(() => {
    const fetchDashboard = async () => {
      const idForDashboard = coupleId || user?.id
      if (!idForDashboard) return

      try {
        setLoading(true)
        setError(null)
        // Pass coupleId when available (couple role), otherwise fallback to user id
        const [dashboardResponse, historyResponse] = await Promise.all([
          analyticsApi.getUserDashboard(coupleId || undefined),
          coupleId ? assessmentApi.assessmentHistory(coupleId).catch(() => null) : Promise.resolve(null),
        ])
        const data = (dashboardResponse as { data?: UserDashboardData }).data || (dashboardResponse as unknown as UserDashboardData)
        setDashboardData(data)

        if (historyResponse) {
          const history = (historyResponse as { data?: AssessmentHistoryItem[] }).data || (historyResponse as unknown as AssessmentHistoryItem[])
          setAssessmentHistory(Array.isArray(history) ? history : [])
        }
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load dashboard data')
        console.error('Dashboard fetch error:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchDashboard()
  }, [user?.id, coupleId])

  const formatDuration = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds <= 0) return '—'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  return (
    <main className='w-full min-h-screen bg-[#EDEFF3]'>
      {/* Main Content Area */}
      <div className='p-[30px]'>
        {/* Top Section: Header + Welcome + Assessment - All in one white container */}
        <div className='bg-white rounded-[24px] p-8 mb-6'>
          {/* Header */}
          <div className='flex items-center justify-end gap-4 mb-8'>
            {/* User Profile */}
            <div className='flex items-center gap-3'>
              <div className='w-[48px] h-[47px] bg-[#434343] rounded-full flex items-center justify-center'>
                <span className='text-[16px] font-bold text-white'>{initials}</span>
              </div>
              <span className='text-[24px] font-bold text-[#353535]'>{displayName}</span>
            </div>

            {/* Menu Dots */}
            <div className='w-[41px] h-[39px] bg-[#e2e3e7] rounded-full flex items-center justify-center cursor-pointer'>
              <svg width='4' height='18' viewBox='0 0 4 18' fill='#434343'>
                <circle cx='2' cy='2' r='2' />
                <circle cx='2' cy='9' r='2' />
                <circle cx='2' cy='16' r='2' />
              </svg>
            </div>
          </div>

          {/* Welcome + Assessment */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
            <WelcomeHero
              userName={displayName}
              subtitle='Every step you take here helps build a stronger, healthier marriage.'
            />
            {loading ? (
              <div className='bg-[#F3F4F6] rounded-[20px] p-8 flex items-center justify-center min-h-[329px]'>
                <p className='text-[#4e4d4d] text-[16px]'>Loading assessment...</p>
              </div>
            ) : error ? (
              <div className='bg-[#F3F4F6] rounded-[20px] p-8 flex items-center justify-center min-h-[329px]'>
                <p className='text-red-600 text-[16px]'>{error}</p>
              </div>
            ) : dashboardData?.assessment ? (
              <AssessmentCard
                sectionsCompleted={dashboardData.assessment.sectionsCompleted}
                totalSections={dashboardData.assessment.totalSections}
                progressPercentage={dashboardData.assessment.progressPercentage}
                status={dashboardData.assessment.status}
                completedAt={dashboardData.assessment.completedAt ?? undefined}
                attemptNumber={assessmentHistory.length > 0 ? assessmentHistory[0]?.attemptNumber : undefined}
                totalAttempts={assessmentHistory.filter(a => a.status === 'completed').length}
                privacyNote='Take your time — your answers are private and only visible to your counsellor.'
                onContinue={() => navigate('/assessment')}
                onRetake={() => navigate('/assessment')}
                onViewResults={() => navigate('/results')}
              />
            ) : (
              <AssessmentCard
                status='not_started'
                privacyNote='Take your time — your answers are private and only visible to your counsellour.'
                onContinue={() => navigate('/assessment')}
              />
            )}
          </div>
        </div>

        {/* Bottom Section: Learning Path + Results */}
        <div className='grid grid-cols-1 xl:grid-cols-2 gap-6'>
          {/* Learning Path Card */}
          {loading ? (
            <div className='bg-white rounded-[24px] p-8 flex items-center justify-center min-h-[487px]'>
              <p className='text-[#4e4d4d] text-[16px]'>Loading learning path...</p>
            </div>
          ) : dashboardData?.learningPath ? (
            <LearningPath
              modulesCompleted={dashboardData.learningPath.completedModules}
              totalModules={dashboardData.learningPath.totalModules}
              modules={dashboardData.learningPath.modules.flatMap((module, moduleIndex) => {
                const thumbnail = module.thumbnail ? resolveBackendUrl(module.thumbnail) : '/placeholder.svg'
                if (!module.videos || module.videos.length === 0) {
                  return [{
                    id: module.moduleId || module.id || `module-${moduleIndex + 1}`,
                    title: module.title,
                    thumbnail,
                    duration: '—',
                    moduleNumber: moduleIndex + 1,
                    isCompleted: module.isCompleted,
                  }]
                }
                return module.videos
                  .slice()
                  .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
                  .map((video, videoIndex) => ({
                    id: video.id || `${module.moduleId || module.id}-video-${videoIndex + 1}`,
                    title: video.title || module.title,
                    thumbnail: video.thumbnail ? resolveBackendUrl(video.thumbnail) : thumbnail,
                    duration: formatDuration(video.duration || 0),
                    moduleNumber: video.orderIndex ?? videoIndex + 1,
                    isCompleted: module.isCompleted,
                  }))
              })}
              onViewAll={() => navigate('/learning-path')}
            />
          ) : (
            <div className='bg-white rounded-[24px] p-8 flex items-center justify-center min-h-[487px]'>
              <p className='text-[#4e4d4d] text-[16px]'>No learning path data available</p>
            </div>
          )}

          {/* Results Card */}
          {loading ? (
            <div className='bg-white rounded-[24px] p-8 flex items-center justify-center min-h-[489px]'>
              <p className='text-[#4e4d4d] text-[16px]'>Loading results...</p>
            </div>
          ) : (() => {
            // Check if there's any completed assessment (from history or dashboard data)
            const latestCompleted = assessmentHistory.find(a => a.status === 'completed')
            const hasCompletedAssessment = (dashboardData?.assessment?.status === 'completed' && dashboardData?.assessmentResults) || latestCompleted
            const overallRisk = dashboardData?.assessmentResults?.overallRisk || latestCompleted?.overallRisk

            return hasCompletedAssessment && overallRisk ? (
              <ResultsCard
                riskLevel={overallRisk}
                onViewFullResults={() => navigate('/results')}
              />
            ) : (
              <ResultsCard
                showPlaceholder={true}
                onViewFullResults={() => navigate('/results')}
              />
            )
          })()}
        </div>
      </div>
    </main>
  )
}
